import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Youtube, Music2 } from "lucide-react";
import api from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import EpisodeResources from "@/components/site/EpisodeResources";

export default function Episodios() {
  const [eps, setEps] = useState([]);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    document.title = "Episodios — Modo Pro";
    api.get("/episodes").then((r) => setEps(r.data)).catch(() => {});
    api.get("/recursos").then((r) => setResources(r.data)).catch(() => {});
  }, []);

  const published = eps.filter((e) => e.is_published);
  const coming = eps.filter((e) => !e.is_published);

  return (
    <main data-testid="episodios-page" className="bg-mp-black min-h-screen pt-32 md:pt-40">
      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 pb-16">
        <div className="mp-eyebrow mb-6">Episodios</div>
        <h1 className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.02em] max-w-3xl">
          Conversaciones para <span className="text-mp-orange">entender mejor</span> y decidir mejor.
        </h1>
        <p className="mt-6 text-white/70 max-w-2xl">
          Diálogos con empresarios, emprendedores, ejecutivos, médicos, creadores y expertos.
          El archivo se actualiza con cada nueva entrega.
        </p>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 pb-24">
        {eps.length === 0 && (
          <div className="border border-white/10 p-16 text-center bg-mp-off">
            <div className="mp-eyebrow">Próximamente</div>
            <p className="mt-4 font-display text-white text-2xl">Los primeros episodios están en producción.</p>
          </div>
        )}

        {published.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {published.map((ep) => <EpisodeCard key={ep.id} ep={ep} resources={resources} />)}
          </div>
        )}

        {coming.length > 0 && (
          <div className="mt-24">
            <div className="mp-eyebrow mb-6">Próximamente</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
              {coming.map((ep) => <EpisodeCard key={ep.id} ep={ep} />)}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

function EpisodeCard({ ep, resources }) {
  const hasLink = ep.youtube_url || ep.spotify_url;
  const cover = ep.thumbnail_url || ASSETS.studioEmpty;
  return (
    <article data-testid={`ep-card-${ep.id}`} className="group">
      <div className="relative aspect-[16/10] overflow-hidden border border-white/10 bg-mp-charcoal">
        <img src={cover} alt={ep.title} className="h-full w-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        {!ep.is_published && (
          <div className="absolute top-3 left-3 bg-mp-orange text-mp-black font-display text-[10px] tracking-[0.24em] uppercase px-3 py-1">
            Próximamente
          </div>
        )}
      </div>
      <div className="mt-5">
        <div className="text-xs tracking-[0.24em] uppercase text-mp-orange font-display">{ep.profession}</div>
        <h3 className="mt-2 font-display text-white text-xl md:text-2xl leading-tight tracking-[-0.01em]">{ep.title}</h3>
        <p className="mt-1 text-mp-mute text-sm">Con {ep.guest_name}</p>
        {ep.description && <p className="mt-3 text-white/70 text-sm leading-relaxed line-clamp-3">{ep.description}</p>}
        {ep.published_at && <p className="mt-3 text-mp-dim text-xs tracking-wider uppercase">{new Date(`${ep.published_at.slice(0, 10)}T12:00:00`).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" })}</p>}
        <div className="mt-5 flex items-center gap-4 flex-wrap">
          {ep.is_published && ep.youtube_url && (
            <a href={ep.youtube_url} target="_blank" rel="noreferrer" data-testid={`ep-yt-${ep.id}`} className="mp-link-underline text-white text-xs font-display tracking-[0.24em] uppercase inline-flex items-center gap-2">
              <Youtube size={14} /> YouTube <ArrowRight size={12} />
            </a>
          )}
          {ep.is_published && ep.spotify_url && (
            <a href={ep.spotify_url} target="_blank" rel="noreferrer" data-testid={`ep-sp-${ep.id}`} className="mp-link-underline text-white text-xs font-display tracking-[0.24em] uppercase inline-flex items-center gap-2">
              <Music2 size={14} /> Spotify
            </a>
          )}
          {!hasLink && (
            <span data-testid={`ep-soon-${ep.id}`} className="text-mp-mute text-xs font-display tracking-[0.24em] uppercase">Próximamente</span>
          )}
        </div>
        <EpisodeResources ep={ep} resources={resources} />
      </div>
    </article>
  );
}
